/**
 * Placeholder de una pantalla que todavia no existe.
 *
 * Por que un placeholder y no simplemente no listar la seccion: la ruta ya
 * existe, el menu ya la muestra y el deploy ya la sirve. Lo unico que falta es
 * el componente. Dejarla registrada apuntando aca convierte "agregar una
 * pantalla" en "crear un archivo", sin tocar routing, layout ni navegacion.
 *
 * La regla que este cartel hace cumplir: **nunca datos inventados**. Una
 * pantalla con numeros mockeados parece que anda y miente; esta dice
 * exactamente que no anda y que falta para que ande.
 */

/**
 * Descripcion de una pantalla pendiente.
 *
 * Vive aca y no en `routes.tsx` para que no haya un ciclo de imports:
 * `routes` -> `lazyScreen` -> `UnderConstruction`, en una sola direccion.
 */
export interface ScreenBlueprint {
  /**
   * Nombre del modulo que la va a implementar, sin extension y relativo a
   * `src/screens/`. Es la clave que usa `lazyScreen.tsx` para encontrarla.
   */
  readonly module: string;
  /** Titulo visible de la seccion. */
  readonly title: string;
  /** Que va a hacer, y que parte del GUI de escritorio reemplaza. */
  readonly purpose: string;
  /** Advertencias para quien la escriba (endpoints que faltan, deuda conocida). */
  readonly notes: readonly string[];
}

export type MissingReason = "missing" | "no-export";

export function UnderConstruction({
  blueprint,
  reason,
}: {
  readonly blueprint: ScreenBlueprint;
  readonly reason: MissingReason;
}) {
  const path = `web/src/screens/${blueprint.module}.tsx`;

  return (
    <section className="screen construction">
      <header className="screen__header">
        <h2>{blueprint.title}</h2>
        <p className="screen__lead">En construccion.</p>
      </header>

      <div className="construction__box">
        {reason === "missing" ? (
          <p>
            Esta seccion todavia no tiene pantalla. Para que aparezca alcanza con
            crear <code>{path}</code> y exportar desde ahi un componente{" "}
            <code>{blueprint.module}</code> (o un <code>export default</code>).
            El registro de rutas lo monta solo: no hay que tocar{" "}
            <code>App.tsx</code> ni el layout.
          </p>
        ) : (
          <p>
            <code>{path}</code> existe pero no exporta ningun componente llamado{" "}
            <code>{blueprint.module}</code> ni un <code>export default</code>.
            Mientras tanto se muestra este cartel en vez de una pantalla en
            blanco.
          </p>
        )}

        <p className="construction__purpose">{blueprint.purpose}</p>

        {blueprint.notes.length > 0 ? (
          <ul className="construction__notes">
            {blueprint.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        ) : null}

        <p className="construction__contract">
          Contrato de la pantalla: se monta <strong>sin props</strong>. La tienda
          seleccionada se pide con <code>useScopedStore()</code> (
          <code>src/components/StoreScope.tsx</code>) y los estados de carga,
          error, vacio y sin permiso salen de <code>&lt;Async&gt;</code> (
          <code>src/components/AsyncBoundary.tsx</code>).
        </p>
      </div>
    </section>
  );
}
